import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { getLanguage, type Language, setLanguage, subscribe } from './store';
import {
  hasCatalog,
  loadCatalog,
  translate,
  type MessageKey,
  type Params,
} from './translate';

/** The active language, and a setter that loads the catalogue before switching. */
export function useLanguage(): [Language, (next: Language) => void] {
  const language = useSyncExternalStore(subscribe, getLanguage, getLanguage);

  const change = useCallback((next: Language) => {
    // Load first, switch second — otherwise the interface flashes English on
    // its way to the language the user just asked for.
    void loadCatalog(next).then(() => setLanguage(next));
  }, []);

  return [language, change];
}

/**
 * The translation function, re-created whenever the language changes so that
 * every component using it re-renders.
 */
export function useT(): (key: MessageKey, params?: Params) => string {
  const language = useSyncExternalStore(subscribe, getLanguage, getLanguage);

  // A language whose catalogue is not in yet renders in English for one frame.
  // `ready` exists purely to force the re-render once it lands.
  const [, setReady] = useState(() => hasCatalog(language));
  useEffect(() => {
    if (hasCatalog(language)) return;
    let cancelled = false;
    void loadCatalog(language).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [language]);

  return useCallback(
    (key: MessageKey, params?: Params) => translate(key, params),
    // `language` is not read here, but changing it must produce a new function
    // identity or memoised children keep rendering the old language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language],
  );
}
